<template>
  <div class="uk-container test-kits">
    <UserHeader />
    <h1>My Test Kits</h1>

    <div class="test-kits-layout" uk-grid>
      <div class="uk-width-expand@m">
        <div v-if="!isPro" class="register-promo">
          <div class="ucari-card-register-kit uk-margin-top uk-margin-bottom">
            <div class="ucari-card-title">Have a new UCARI Intolerance Kit? Register now.</div>
            <router-link to="/register-kit" class="uk-button uk-button-primary">Register Kit</router-link>
          </div>
        </div>

        <div v-else class="search-row">
          <div style="position: relative; max-width: 400px;">
            <input
              class="uk-input search-test-kits"
              type="text"
              v-model.trim="searchQuery"
              placeholder="Search by last name or kit ID…"
              :style="searchQuery ? 'padding-right: 36px;' : ''"
              @keyup.enter="applySearch"
            />
            <a
              v-if="searchQuery"
              @click="clearSearch"
              style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); cursor: pointer; padding: 4px;"
            >
              <span uk-icon="icon: close; ratio: 0.8"></span>
            </a>
          </div>
        </div>

        <div class="uk-align-center">
          <div v-if="filteredData && filteredData.length">
            <div class="ucari-card" v-for="item in filteredData" :key="item.KitId">
              <div class="test-kit-card-header">
                <div>
                  <div class="test-kit-card-eyebrow">
                    <span v-if="item.TypeName">{{ item.TypeName }} Intolerance Kit</span>
                    <span v-if="item.TypeName" class="test-kit-card-divider">/</span>
                    <span>{{ item.TypeDescription === 'Pets' ? 'Pet' : 'Personal' }}</span>
                  </div>
                  <div class="ucari-card-title">
                    {{ displayRegistrantName(item) }}
                  </div>
                </div>

                <div
                  v-if="statusLabel(item)"
                  class="test-kit-status-pill"
                  :class="{
                    complete: item.ResultDateUtc,
                    received: item.ReceivedDateUtc && !item.ResultDateUtc
                  }"
                >
                  {{ statusLabel(item) }}
                </div>
              </div>

              <div class="test-kit-card-meta">
                <div class="test-kit-meta-item test-kit-grid-column--1">
                  <label>Kit ID</label>
                  <div>{{ item.SerialNumber || 'N/A' }}</div>
                </div>

                <div v-if="item.TypeName" class="test-kit-meta-item test-kit-grid-column--2">
                  <label>Test Type</label>
                  <div>{{ item.TypeName }}</div>
                </div>

                <div v-if="isPetRegistration(item) && item.PetType" class="test-kit-meta-item test-kit-grid-column--3">
                  <label>Pet Type</label>
                  <div>{{ item.PetType }}</div>
                </div>

                <div v-if="!isPetRegistration(item) && item.Gender" class="test-kit-meta-item test-kit-grid-column--3">
                  <label>Gender</label>
                  <div>{{ item.Gender }}</div>
                </div>
              </div>

              <div class="test-kit-card-dates">
                <div class="test-kit-date-item test-kit-grid-column--1">
                  <label>Registered On</label>
                  <div v-if="item.RegisteredOnUtc">
                    {{ convertUtcToLocalDateOnly(item.RegisteredOnUtc) }}
                  </div>
                  <div v-else>--</div>
                </div>

                <div class="test-kit-date-item test-kit-grid-column--2">
                  <label>Received On</label>
                  <div v-if="item.ReceivedDateUtc">
                    {{ convertUtcToLocalDateOnly(item.ReceivedDateUtc) }}
                  </div>
                  <div v-else>--</div>
                </div>

                <div class="test-kit-date-item test-kit-grid-column--3">
                  <label>Result Posted On</label>
                  <div v-if="item.ResultDateUtc">
                    {{ convertUtcToLocalDateOnly(item.ResultDateUtc) }}
                  </div>
                  <div v-else>--</div>
                </div>

                <div
                  v-if="item.ResultDateUtc"
                  class="test-kit-date-item test-kit-grid-column--4"
                  :class="`test-kit-expiration--${resultsExpirationState(item.ResultDateUtc)}`"
                >
                  <label>{{ resultsExpirationLabel(item.ResultDateUtc) }}</label>
                  <div>{{ formatResultsExpirationDate(item.ResultDateUtc) }}</div>
                </div>
              </div>

              <div v-if="item.IngredientCheckerEntitlementLabel" class="uk-alert-success uk-padding-small uk-margin-small-top">
                <strong>{{ item.IngredientCheckerEntitlementLabel }}</strong>
                <div v-if="item.IngredientCheckerEntitlementStatus === 'Pending Results'" class="uk-text-small">
                  Access begins when test results are ready.
                </div>
                <div v-else-if="item.IngredientCheckerEntitlementStatus === 'Expired' && item.IngredientCheckerEntitlementEndsUtc" class="uk-text-small">
                  Expired {{ formatEntitlementDate(item.IngredientCheckerEntitlementEndsUtc) }}
                </div>
                <div v-else-if="item.IngredientCheckerEntitlementEndsUtc" class="uk-text-small">
                  Available through {{ formatEntitlementDate(item.IngredientCheckerEntitlementEndsUtc) }}
                </div>
              </div>

              <div class="test-kit-card-actions">
                <button
                  class="test-kit-edit-link"
                  type="button"
                  uk-toggle="target: #edit-profile-modal"
                  uk-tooltip="Edit registration details"
                  @click="selectItem(item)"
                >
                  <img
                    src="/images/edit.svg"
                    alt=""
                    class="action-icon"
                  />
                  Edit
                </button>
              </div>

              <div class="test-kit-card-buttons">
                <a v-if="item.ResultDateUtc" :href="`/test-results?id=${item.KitId}`" class="uk-button uk-button-primary uk-margin-small-right uk-margin-small-bottom">
                  View Test Results
                </a>
                <router-link v-if="item.ResultDateUtc && isIngredientCheckerEligible(item)" :to="{ path: '/ingredient-checker', query: { id: item.KitId } }" class="uk-button uk-button-secondary uk-margin-small-right uk-margin-small-bottom">
                  {{ ingredientCheckerButtonText(item) }}
                </router-link>
                <a
                  v-if="item.ResultDateUtc && shouldPromptRepurchase(item.ResultDateUtc)"
                  :href="repurchaseUrl(item)"
                  class="uk-button uk-button-default test-kit-buy-again uk-margin-small-right uk-margin-small-bottom"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Buy Again
                </a>
                <!--
                <button
                  v-if="item.ResultDateUtc && isPro"
                  class="uk-button uk-button-primary uk-margin-small-right uk-margin-small-bottom"
                  :disabled="loadingPDF[item.KitId]"
                  @click="downloadPDF(item)"
                >
                  <span v-if="!loadingPDF[item.KitId]">Download PDF</span>
                  <span v-else><span uk-spinner="ratio: 0.6"></span> Generating...</span>
                </button>
                <button
                  v-if="isAllowedCid && item.ResultDateUtc"
                  class="uk-button uk-button-secondary uk-margin-small-right uk-margin-small-bottom"
                  @click="downloadCSV(item)"
                >
                  Download CSV
                </button>
                -->
              </div>
            </div>
          </div>
          <p v-else-if="isLoading">Loading...</p>
          <p v-else-if="error">There was an error loading the test kits. Please try again later.</p>
          <p v-else>No test kits found.</p>
        </div>

        <div class="uk-flex uk-flex-center uk-margin-large-top" v-if="totalPages > 1">
          <button
            class="uk-button uk-button-default uk-margin-small-right"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            Previous
          </button>
          <button
            class="uk-button uk-button-default"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            Next
          </button>
        </div>
        <div v-if="totalPages > 1" class="uk-margin-large-top uk-text-center">
          <div class="uk-text-small uk-margin-small-top">
            Page {{ currentPage }} of {{ totalPages }}
          </div>
        </div>
      </div>

      <aside class="uk-width-1-3@m test-kits-top-promos">
        <IngredientCheckerPromo source="test-kits" />
        <div v-if="showNutrable" class="uk-margin-small-top">
          <NutrableProgramPromo source="test-kits" />
        </div>
        <div class="uk-margin-small-top">
          <PetVetPromo source="test-kits" />
        </div>
        <div class="uk-margin-small-top">
          <WellnessJourneyPromo />
        </div>
      </aside>
    </div>
  </div>
  <UcariFooter />
  <EditProfileModal
    v-if="selectedItem"
    :item="selectedItem"
    @close="closeModal"
    @item-updated="updateItem"
  />
</template>

<script>
import { canAccessNutrable } from '@/utils/nutrable';
import axios from 'axios';
import UserHeader from './UserHeader.vue';
import UcariFooter from './UcariFooter.vue';
import IngredientCheckerPromo from '@/components/IngredientCheckerPromo.vue';
import NutrableProgramPromo from '@/components/NutrableProgramPromo.vue';
import PetVetPromo from '@/components/PetVetPromo.vue';
import WellnessJourneyPromo from '@/components/WellnessJourneyPromo.vue';
import EditProfileModal from '@/components/EditProfileModal.vue';
import { convertUtcToLocalDateOnly, shouldHideIngredientChecker } from '@/utils/common';

export default {
  created() {
    document.title = 'My Test Kits | UCARI';
    this.loadCredentials();
  },
  components: {
    UserHeader,
    UcariFooter,
    IngredientCheckerPromo,
    NutrableProgramPromo,
    PetVetPromo,
    WellnessJourneyPromo,
    EditProfileModal
  },
  data() {
    return {
      data: [],
      isLoading: false,
      loadingPDF: {},
      error: false,
      accessToken: '',
      cid: '',
      isPro: false,
      showNutrable: false,
      searchQuery: '',
      searchTimer: null,
      selectedItem: null,
      petName: '',
      petType: 'Dog',
      firstName: '',
      lastName: '',
      currentPage: 1,
      totalPages: 1,
      pageSize: 350,
    };
  },
  watch: {
    searchQuery() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchItems();
      }, 350);
    }
  },
  mounted() {
    canAccessNutrable().then(allowed => { this.showNutrable = allowed; });
    this.fetchItems();
  },
  computed: {
    filteredData() {
      const items = this.data.items || [];
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (!q) return items;

      return items.filter(item => {
        const last = (item.LastName || '').toLowerCase();
        const serial = String(item.SerialNumber || '').toLowerCase();
        const kitId = String(item.KitId || '').toLowerCase();
        return last.includes(q) || serial.includes(q) || kitId.includes(q);
      });
    },
    isAllowedCid() {
      const allowedCids = [
        '75980601-D77C-47DF-982B-7BBA3AD10D2A',
        'BA4A53C0-BEDD-4CAC-A485-A3C08497B0C0',
        'A495433D-65E0-4EE2-8506-E27A7CDEB124',
        '8C5052CD-0EDC-4914-83FB-D5D04008C62D'
      ];
      return allowedCids.includes(this.cid);
    },
    isIngredientReader() {
      const allowedCids = [
        'BA4A53C0-BEDD-4CAC-A485-A3C08497B0C0',
        '620659CA-AB8F-458E-A7C8-84D036ABD269',
        'D06C1B8D-80B9-49D3-B756-EA8E68B295A1',
        'A495433D-65E0-4EE2-8506-E27A7CDEB124',
        '8C5052CD-0EDC-4914-83FB-D5D04008C62D'
      ];
      return allowedCids.includes(this.cid);
    },
    isAllowedPDF() {
      const allowedCids = [
        'BA4A53C0-BEDD-4CAC-A485-A3C08497B0C0',
        '620659CA-AB8F-458E-A7C8-84D036ABD269',
        'D06C1B8D-80B9-49D3-B756-EA8E68B295A1',
        'A495433D-65E0-4EE2-8506-E27A7CDEB124',
        '8C5052CD-0EDC-4914-83FB-D5D04008C62D'
      ];
      return allowedCids.includes(this.cid);
    },
  },
  methods: {
    isPetRegistration(item) {
      return item.TypeDescription === 'Pets' || !!item.PetName;
    },
    displayRegistrantName(item) {
      const ownerName = [item.FirstName, item.LastName].filter(Boolean).join(' ').trim();
      if (this.isPetRegistration(item) && item.PetName) {
        return `${item.PetName}${ownerName ? ` · ${ownerName}` : ''}`;
      }
      return ownerName || 'Unnamed registration';
    },
    statusLabel(item) {
      if (item.ResultDateUtc) return 'Complete';
      if (item.ReceivedDateUtc) return 'Received';
      if (item.RegisteredOnUtc) return 'Registered';
      return '';
    },
    hasFreeIngredientChecker(item) {
      return Boolean(item?.HasFreeIngredientChecker);
    },
    ingredientCheckerButtonText(item) {
      return this.hasFreeIngredientChecker(item) ? 'Open Ingredient Checker' : 'Ingredient Checker';
    },
    formatEntitlementDate(value) {
      return new Date(value).toLocaleDateString();
    },
    formatResultsExpirationDate(resultDateUtc) {
      const expirationDate = this.resultsExpirationDate(resultDateUtc);
      return expirationDate ? convertUtcToLocalDateOnly(expirationDate.toISOString()) : '';
    },
    resultsExpirationDate(resultDateUtc) {
      const resultDate = new Date(resultDateUtc);
      if (Number.isNaN(resultDate.getTime())) return null;

      const expirationDate = new Date(resultDate);
      expirationDate.setUTCFullYear(expirationDate.getUTCFullYear() + 1);
      return expirationDate;
    },
    resultsExpirationState(resultDateUtc) {
      const expirationDate = this.resultsExpirationDate(resultDateUtc);
      if (!expirationDate) return 'active';

      const now = new Date();
      if (now > expirationDate) return 'expired';

      const expiringSoonDate = new Date(expirationDate);
      expiringSoonDate.setUTCDate(expiringSoonDate.getUTCDate() - 30);
      return now > expiringSoonDate ? 'expiring-soon' : 'active';
    },
    resultsExpirationLabel(resultDateUtc) {
      const state = this.resultsExpirationState(resultDateUtc);
      if (state === 'expired') return 'Expired';
      if (state === 'expiring-soon') return 'Expiring Soon';
      return 'Expires On';
    },
    shouldPromptRepurchase(resultDateUtc) {
      return this.resultsExpirationState(resultDateUtc) !== 'active';
    },
    repurchaseUrl(item) {
      return this.isPetRegistration(item)
        ? 'https://www.ucari.com/products/pet-intolerance-test-kit'
        : 'https://www.ucari.com/products/intolerance-test-kit';
    },
    isIngredientCheckerEligible(item) {
      if (shouldHideIngredientChecker(this.cid)) return false;
      return !!item?.ResultDateUtc;
    },
    loadCredentials() {
      this.accessToken = localStorage.getItem('accessToken');
      this.cid = localStorage.getItem('cid');
      const isProValue = localStorage.getItem('isPro');
      this.isPro = isProValue === '1' || isProValue === 'true';
    },
    applySearch() {
      this.currentPage = 1;
      this.fetchItems();
    },
    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.fetchItems();
    },
    async fetchItems() {
      try {
        this.isLoading = true;

        const q = (this.searchQuery || '').trim();

        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/test-kits`, {
          params: {
            page: this.currentPage,
            pageSize: this.pageSize,
            q: q || undefined
          },
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          }
        });

        this.data = response.data;
        this.totalPages = response.data.totalPages || 1;
      } catch (error) {
        console.error('There was an error fetching the data!', error);
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },
    async downloadCSV(item) {
      const kitId = item.KitId;
      const serialNumber = item.SerialNumber;
      const resultDateUtc = item.ResultDateUtc;

      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/test-results-by-level?id=${encodeURIComponent(kitId)}&resultDateUtc=${encodeURIComponent(resultDateUtc)}&level=all&format=csv`,
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'cid': this.cid
            }
          }
        );

        if (!response.ok) throw new Error('CSV download failed');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${serialNumber}.csv`;
        link.click();
      } catch (err) {
        console.error('Download failed:', err);
        alert('There was an error downloading your CSV. Please try again.');
      }
    },
    async downloadPDF(item) {
      const kitId = String(item.KitId);
      this.loadingPDF = { ...this.loadingPDF, [kitId]: true };

      try {
        const resp = await fetch(
          `${process.env.VUE_APP_API_URL}/api/generate-pdf?kitId=${encodeURIComponent(kitId)}&download=1`,
          { headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid } }
        );
        if (!resp.ok) throw new Error('PDF request failed');

        let { blobUrl, fileName } = await resp.json();
        if (!blobUrl) throw new Error('No blobUrl returned');

        blobUrl = blobUrl.replace(
          'https://ucariblob.blob.core.windows.net',
          'https://files.ucari.com'
        );

        try {
          const fileRes = await fetch(blobUrl, { mode: 'cors', credentials: 'omit' });
          if (!fileRes.ok) throw new Error(`blob fetch failed: ${fileRes.status} ${fileRes.statusText}`);

          const pdfBlob = await fileRes.blob();
          const objectUrl = URL.createObjectURL(pdfBlob);

          const filename = fileName || (item.SerialNumber ? `${item.SerialNumber}.pdf` : `ucari-${kitId}.pdf`);

          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objectUrl);
        } catch (downloadErr) {
          const win = window.open(blobUrl, '_blank');
          if (!win) {
            const a = document.createElement('a');
            a.href = blobUrl;
            a.target = '_blank';
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
        }
      } catch (e) {
        console.error('Download failed:', e);
        alert('There was an error downloading your PDF. Please try again.');
      } finally {
        this.loadingPDF = { ...this.loadingPDF, [kitId]: false };
      }
    },
    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.currentPage = newPage;
        this.fetchItems();
      }
    },
    selectItem(item) {
      this.selectedItem = item;
    },
    closeModal() {
      this.selectedItem = null;
    },
    updateItem(updatedItem) {
      const index = this.data.items.findIndex(item => item.KitId === updatedItem.KitId);
      console.log(index);
      if (index !== -1) {
        this.data.items[index] = updatedItem;
      }
      console.log(index);
      this.closeModal();
    },
    convertUtcToLocalDateOnly
  }
};
</script>

<style scoped>
.test-kits {
  padding-bottom: 24px;
}

.test-kits-layout {
  column-gap: 32px;
}

.test-kits-top-promos {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}

.search-row {
  margin-top: 8px;
}

.search-row + .uk-align-center {
  margin-top: 8px;
}

.register-promo {
  margin-bottom: 8px;
}

.test-kit-card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.test-kit-card-eyebrow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71806e;
}

.test-kit-card-divider {
  opacity: 0.6;
}

.test-kit-status-pill {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #eef1eb;
  color: #536051;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.test-kit-status-pill.received {
  background: #f2efe6;
  color: #8d6a22;
}

.test-kit-status-pill.complete {
  background: #e9f6ea;
  color: #245c2b;
}

.test-kit-card-meta,
.test-kit-card-dates {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 24px;
  row-gap: 12px;
}

.test-kit-card-meta {
  margin-top: 18px;
}

.test-kit-meta-item,
.test-kit-date-item {
  min-width: 0;
}

.test-kit-grid-column--1 {
  grid-column: 1;
}

.test-kit-grid-column--2 {
  grid-column: 2;
}

.test-kit-grid-column--3 {
  grid-column: 3;
}

.test-kit-grid-column--4 {
  grid-column: 4;
}

.test-kit-meta-item label,
.test-kit-date-item label {
  font-weight: 700;
  }

.test-kit-card-dates {
  margin-top: 12px;
}

.test-kit-expiration--expiring-soon label,
.test-kit-expiration--expiring-soon div {
  color: #8d6a22;
}

.test-kit-expiration--expired label,
.test-kit-expiration--expired div {
  color: #a4382f;
}

.test-kit-card-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.test-kit-edit-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #536051;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.test-kit-card-buttons {
  margin-top: 12px;
}

.test-kit-buy-again {
  border-color: #536051;
  color: #536051;
  font-weight: 600;
}

@media (min-width: 960px) {
  .test-kits-layout {
    align-items: flex-start;
  }

}

@media (max-width: 959px) {
  .test-kits-layout {
    column-gap: 0;
  }

  .test-kit-card-header {
    flex-direction: column;
  }

  .test-kit-card-actions {
    justify-content: flex-start;
  }

  .test-kits-top-promos {
    margin-top: 16px;
    margin-bottom: 24px;
  }
}

@media (max-width: 639px) {
  .test-kit-card-meta,
  .test-kit-card-dates {
    grid-template-columns: 1fr;
  }

  .test-kit-grid-column--1,
  .test-kit-grid-column--2,
  .test-kit-grid-column--3,
  .test-kit-grid-column--4 {
    grid-column: auto;
  }
}
</style>
